import React, { useState, useEffect } from 'react';
import CaretIcon from '../../../../../svg/CaretIcon';
import { transformPermissionsToBackend } from '../../../../../utils/utils';

const PermissionsManager = ({ initialPermissions, onChange }) => {
    const [permissions, setPermissions] = useState(initialPermissions || []);
    const [expanded, setExpanded] = useState({});

    useEffect(() => {
        setExpanded({});
    }, []); 

    useEffect(() => {
        if(initialPermissions) {
            setPermissions(initialPermissions);
        }
    }, [initialPermissions]);

    const toggleExpand = (id) => {
        setExpanded(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };
    // Helper function to update all children recursively with the same checked state
    const updateAllChildrenChecked = (items, checked) => {
        return items.map(item => ({
            ...item,
            checked,
            indeterminate: false,
            children: item.children && item.children.length > 0
                ? updateAllChildrenChecked(item.children, checked)
                : item.children
        }));
    };

    // Update the checked status of an item and all its children and parents
    const updateCheckedStatus = (items, itemId, checked) => {
        return items.map(item => {
            if (item.id === itemId) {
                // Update this item and all its descendants
                let updatedItem = {
                    ...item,
                    checked,
                    indeterminate: false
                };

                // If this item has children, update all of them too
                if (item.children && item.children.length > 0) {
                    updatedItem.children = updateAllChildrenChecked(item.children, checked);
                }

                return updatedItem;
            } else if (item.children && item.children.length > 0) {
                // Check if any of this item's children need to be updated
                const updatedChildren = updateCheckedStatus(item.children, itemId, checked);

                // Calculate parent state based on children
                const allChildrenChecked = updatedChildren.every(child => child.checked);
                const anyChildrenChecked = updatedChildren.some(child => child.checked || child.indeterminate);

                return {
                    ...item,
                    checked: allChildrenChecked,
                    indeterminate: !allChildrenChecked && anyChildrenChecked,
                    children: updatedChildren
                };
            }
            return item;
        });
    };

    // Handle checkbox change
    const handleCheckboxChange = (id, checked) => {
        const updatedPermissions = updateCheckedStatus(permissions, id, checked);
        setPermissions(updatedPermissions);
      
        if (onChange) {
          onChange(updatedPermissions);
        }
    };

    // Render the tree structure
    const renderTree = (items, level = 0) => {
        return items.map(item => (
            <div key={item.id} className="permission-item" style={{ paddingLeft: `${level * 12}px` }}>
                <div className="permission-row d-flex align-items-start w-100">
                    {/* Caret Icon */}
                    {item.children && item.children.length > 0 ? (
                        <span
                            className="expand-icon d-flex align-items-center justify-content-center me-2"
                            onClick={() => toggleExpand(item.id)}
                            style={{ width: "24px", flexShrink: 0 }}
                        >
                            <CaretIcon direction={expanded[item.id] ? "down" : "left"} />
                        </span>
                    ) : (
                        <span className="expand-placeholder me-2" style={{ width: "24px", flexShrink: 0 }}></span>
                    )}

                    <div className="form-check custom-checkbox checkbox-info d-flex align-items-start" style={{ flexGrow: 1 }}>
                        <input
                            type="checkbox"
                            className="form-check-input"
                            id={item.id}
                            checked={item.checked}
                            onChange={(e) => handleCheckboxChange(item.id, e.target.checked)}
                            ref={(el) => {
                                if (el) {
                                    el.indeterminate = item.indeterminate || false;
                                }
                            }}
                            style={{ flexShrink: 0, marginTop: "0.25rem" }}
                        />
                        <label
                            className="form-check-label ms-2"
                            htmlFor={item.id}
                            style={{
                                wordBreak: "break-word",
                                hyphens: "auto",
                                overflowWrap: "break-word"
                            }}
                        >
                            {item.label}
                        </label>
                    </div>
                </div>

                {item.children && item.children.length > 0 && expanded[item.id] && (
                    <div className="permission-children">
                        {renderTree(item.children, level + 1)}
                    </div>
                )}
            </div>


        ));
    };

    return (
        <>
            {renderTree(permissions)}
        </>
    );
};

export default PermissionsManager;